import { Button } from '@1trading/react';
import { useMemo } from 'react';
import { type AlertType, BULK_DISMISS_MAX_ALERTS, type ComplianceAlert, type MuteDuration } from '@/services/api/compliance-alerts';
import { pluralize } from '@/utils/string';
import { DismissAlertsButtons, type DismissCopy, useDismissAlerts } from './dismiss-alerts';
import { type DismissSelectionSummary, describeDismissal, describeDismissSelection, summariseDismissSelection } from './dismiss-selection';

const SelectionMuteSummary = ({ summary, muteDuration }: { summary: DismissSelectionSummary; muteDuration: MuteDuration }) => (
  <div className="flex flex-col gap-2 text-sm">
    <p>
      {summary.alertCount === 1
        ? 'Muting this alert means no further alerts for its PID and alert type will be generated'
        : "Muting these alerts means no further alerts for each alert's PID and alert type will be generated"}{' '}
      for {muteDuration}. This covers <span className="font-medium text-primary">{pluralize(summary.pids.length, 'user')}</span> and alert{' '}
      {summary.eventTypes.length > 1 ? 'types' : 'type'} <span className="font-medium text-primary">{summary.eventTypes.join(', ')}</span>.
    </p>
    <div className="max-h-32 overflow-y-auto rounded-md border p-2 font-mono text-xs">
      {summary.pids.map((pid) => (
        <div key={pid}>{pid}</div>
      ))}
    </div>
  </div>
);
SelectionMuteSummary.displayName = 'SelectionMuteSummary';

interface BulkDismissBarProps {
  alertType: AlertType;
  /** Ticked rows passing the current filters; only these are dismissed */
  selectedAlerts: ComplianceAlert[];
  hiddenSelectedCount: number;
  onClearSelection: () => void;
  onDismissed: (alerts: ComplianceAlert[]) => void;
}

export const BulkDismissBar = ({ alertType, selectedAlerts, hiddenSelectedCount, onClearSelection, onDismissed }: BulkDismissBarProps) => {
  const { dismissInBulk } = useDismissAlerts(alertType);

  const summary = useMemo(() => summariseDismissSelection(selectedAlerts), [selectedAlerts]);

  const selectedCount = selectedAlerts.length;
  const isOverLimit = selectedCount > BULK_DISMISS_MAX_ALERTS;
  const copy: DismissCopy = {
    noun: selectedCount === 1 ? 'Alert' : 'Alerts',
    describe: (muteDuration) => describeDismissal(describeDismissSelection(summary), muteDuration),
    muteTitle: `Dismiss and Mute ${pluralize(selectedCount, 'Alert')}`,
    renderMuteSummary: (muteDuration) => <SelectionMuteSummary muteDuration={muteDuration} summary={summary} />,
  };

  const handleDismiss = async (justification: string, muteExpiry?: number) => {
    await dismissInBulk(selectedAlerts, justification, muteExpiry);
    onDismissed(selectedAlerts);
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <span className="text-sm text-muted-foreground">
        {selectedCount} selected
        {hiddenSelectedCount > 0 ? `, ${hiddenSelectedCount} hidden by filters` : ''}
      </span>
      {isOverLimit ? <span className="text-sm text-red-500">Select at most {BULK_DISMISS_MAX_ALERTS} alerts to dismiss</span> : null}
      <Button onClick={onClearSelection} type="button" variant="outline">
        Clear selection
      </Button>
      <DismissAlertsButtons copy={copy} disabled={selectedCount === 0 || isOverLimit} onDismiss={handleDismiss} />
    </div>
  );
};
BulkDismissBar.displayName = 'BulkDismissBar';
